import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";

import HomePage from "@/pages/HomePage";
import WatchSegmentPage from "@/pages/WatchSegmentPage";
import EnginesPage from "@/pages/EnginesPage";
import DemoPage from "@/pages/DemoPage";
import CaseStudyPage from "@/pages/CaseStudyPage";
import ProgramPage from "@/pages/ProgramPage";
import PricingPage from "@/pages/PricingPage";
import FounderPage from "@/pages/FounderPage";

import SignInPage from "@/pages/SignInPage";
import ConsentPage from "@/pages/ConsentPage";
import ProspectWelcomePage from "@/pages/ProspectWelcomePage";
import ForgotPasswordPage from "@/pages/admin/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/admin/ResetPasswordPage";
import AuthCallback from "@/pages/admin/AuthCallback";
import PinSettingsPage from "@/pages/admin/PinSettingsPage";
import BillingPage from "@/pages/admin/BillingPage";
import ConsoleAccountsPage from "@/pages/admin/ConsoleAccountsPage";
import VendorLoginPage from "@/pages/vendor/VendorLoginPage";
import VendorPortalPage from "@/pages/vendor/VendorPortalPage";
import ContractVendorsPage from "@/pages/admin/ContractVendorsPage";
import SmsConsolePage from "@/pages/admin/SmsConsolePage";
import FinanceOverview from "@/pages/admin/finance/OverviewPage";
import FinanceTransactions from "@/pages/admin/finance/TransactionsPage";
import FinanceExpenses from "@/pages/admin/finance/ExpensesPage";
import FinanceBills from "@/pages/admin/finance/BillsPage";
import FinanceDistributions from "@/pages/admin/finance/DistributionsPage";
import FinanceForecast from "@/pages/admin/finance/ForecastPage";
import FinanceReports from "@/pages/admin/finance/ReportsPage";
import ContactsListPage from "@/pages/admin/crm/ContactsListPage";
import ContactDetailPage from "@/pages/admin/crm/ContactDetailPage";
import DocumentCenterPage from "@/pages/admin/documents/DocumentCenterPage";
import TutorialManager from "@/pages/admin/TutorialManager";
import MicroLandingPage from "@/pages/public/MicroLandingPage";
import ResponsePage from "@/pages/public/ResponsePage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import BriefDetail from "@/pages/admin/BriefDetail";
import PreviewManager from "@/pages/admin/PreviewManager";
import LayoutEditorPage from "@/pages/admin/LayoutEditorPage";
import LabelsEditorPage from "@/pages/admin/LabelsEditorPage";
import NdsSchemaEditorPage from "@/pages/admin/NdsSchemaEditorPage";
import DatabaseManagerPage from "@/pages/admin/DatabaseManagerPage";
import RoiDashboardPage from "@/pages/admin/RoiDashboardPage";
import PillarHealthPage from "@/pages/admin/PillarHealthPage";
import LessonVoicePage from "@/pages/admin/LessonVoicePage";
import ArchiveCenterPage from "@/pages/admin/ArchiveCenterPage";
import ArchivedFundsPage from "@/pages/admin/ArchivedFundsPage";
import ResidentPortalPage from "@/pages/ResidentPortalPage";
import PortalLoginPage from "@/pages/PortalLoginPage";
import DocSignPage from "@/pages/DocSignPage";
import PublicSchedulePage, { PublicScheduleCancelPage } from "@/pages/PublicSchedulePage";
import PartnerSetPinPage from "@/pages/partner/PartnerSetPinPage";
import OsPage from "@/pages/OsPage";
import WindowTabPage from "@/pages/WindowTabPage";
import NewBriefPage from "@/pages/NewBriefPage";
import PreviewPublic from "@/pages/PreviewPublic";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import PrivacyPage from "@/pages/PrivacyPage";
import DemoDirectoryPage from "@/pages/DemoDirectoryPage";
import DemoUnlockPage from "@/pages/DemoUnlockPage";
import ClientDemoPage from "@/pages/ClientDemoPage";
import DemoDirectoryManagerPage from "@/pages/admin/DemoDirectoryManagerPage";
import PilotRequestsPage from "@/pages/admin/PilotRequestsPage";
import { FormsPage, RecordsPage } from "@/pages/admin/FormsPage";
import ShareLinkPage from "@/pages/ShareLinkPage";
import DemoPreviewPage from "@/pages/DemoPreviewPage";

import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { PresencePinger } from "@/lib/presence";
import { PreviewBanner, PreviewStart, VendorLoginAsStart } from "@/components/console/PreviewAs";
import LiveUsersPage from "@/pages/admin/LiveUsersPage";
import { AllyContextProvider } from "@/lib/allyContext";
import AllyOS from "@/components/ally/AllyOS";
import { DynamicEngineProvider } from "@/lib/dynamicEngine";
import { ContentProvider } from "@/lib/editable";
import "@/lib/labelKeys";
import LegacySite, { isLegacyPath } from "@/legacy/LegacySite";   // seeds label registry at boot

// Public pages get header + footer; admin/preview/checkout pages don't (they have their own chrome)
// Home ("/") provides its own custom header + footer so we skip the shell chrome there too.
const PublicShell = ({ children, bare = false }) => (
  <>
    {!bare && <Header />}
    <main>{children}</main>
    {!bare && <Footer />}
  </>
);

const AppRoutes = () => {
  const { pathname } = useLocation();
  const bareRoute =
    pathname.startsWith("/admin") ||
    pathname.startsWith("/preview/") ||
    pathname === "/preview-start" || pathname === "/vendor-login-as" ||
    pathname.startsWith("/checkout") ||
    pathname.startsWith("/c/") ||
    pathname.startsWith("/r/") ||
    pathname.startsWith("/os") ||
    pathname.startsWith("/demo") ||
    pathname.startsWith("/demos") ||
    pathname.startsWith("/d/") ||
    pathname.startsWith("/demo/") ||
    pathname.startsWith("/new-brief") ||
    pathname.startsWith("/portal/") ||
    pathname.startsWith("/partner") ||
    pathname.startsWith("/vendor") ||
    pathname.startsWith("/doc/") ||
    pathname.startsWith("/consent/") ||
    pathname.startsWith("/welcome/") ||
    pathname.startsWith("/schedule") ||
    pathname.startsWith("/watch") ||
    pathname === "/privacy" ||
    pathname === "/login" ||
    pathname.startsWith("/login/");

  return (
    <div className="App min-h-screen">
      <PreviewBanner />
      {isLegacyPath(pathname) ? (
        <LegacySite />
      ) : bareRoute ? (
        <Routes>
          {/* Admin */}
          <Route path="/preview-start" element={<PreviewStart />} />
          <Route path="/vendor-login-as" element={<VendorLoginAsStart />} />
          <Route path="/admin/login" element={<SignInPage />} />
          <Route path="/admin/forgot-password" element={<Navigate to="/login" replace />} />
          <Route path="/admin/reset-password" element={<Navigate to="/login" replace />} />
          <Route path="/admin/auth/callback" element={<AuthCallback />} />
          <Route path="/admin/settings" element={<ProtectedRoute><PinSettingsPage /></ProtectedRoute>} />
          <Route path="/admin/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute><ConsoleAccountsPage /></ProtectedRoute>} />
          <Route path="/admin/sms" element={<ProtectedRoute><SmsConsolePage /></ProtectedRoute>} />
          <Route path="/admin/finance" element={<ProtectedRoute><FinanceOverview /></ProtectedRoute>} />
          <Route path="/admin/finance/transactions" element={<ProtectedRoute><FinanceTransactions /></ProtectedRoute>} />
          <Route path="/admin/finance/expenses" element={<ProtectedRoute><FinanceExpenses /></ProtectedRoute>} />
          <Route path="/admin/finance/bills" element={<ProtectedRoute><FinanceBills /></ProtectedRoute>} />
          <Route path="/admin/finance/bank" element={<ProtectedRoute><FinanceBills view="bank" /></ProtectedRoute>} />
          <Route path="/admin/finance/debt" element={<ProtectedRoute><FinanceBills view="debt" /></ProtectedRoute>} />
          <Route path="/admin/finance/distributions" element={<ProtectedRoute><FinanceDistributions /></ProtectedRoute>} />
          <Route path="/admin/finance/forecast" element={<ProtectedRoute><FinanceForecast /></ProtectedRoute>} />
          <Route path="/admin/finance/reports" element={<ProtectedRoute><FinanceReports /></ProtectedRoute>} />
          <Route path="/admin/contacts" element={<ProtectedRoute><ContactsListPage /></ProtectedRoute>} />
          <Route path="/admin/contacts/:id" element={<ProtectedRoute><ContactDetailPage /></ProtectedRoute>} />
          <Route path="/admin/documents" element={<ProtectedRoute><DocumentCenterPage /></ProtectedRoute>} />
          <Route path="/admin/tutorials" element={<ProtectedRoute><TutorialManager /></ProtectedRoute>} />
          <Route path="/c/:token" element={<MicroLandingPage />} />
          <Route path="/r/:token" element={<ResponsePage />} />
          <Route path="/admin" element={<ProtectedRoute><OsPage /></ProtectedRoute>} />
          <Route path="/admin/w/:kind" element={<ProtectedRoute><WindowTabPage /></ProtectedRoute>} />
          <Route path="/admin/briefs" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/brief/:id" element={<ProtectedRoute><BriefDetail /></ProtectedRoute>} />
          <Route path="/admin/previews" element={<ProtectedRoute><PreviewManager /></ProtectedRoute>} />
          <Route path="/admin/layout" element={<ProtectedRoute><LayoutEditorPage /></ProtectedRoute>} />
          <Route path="/admin/labels" element={<ProtectedRoute><LabelsEditorPage /></ProtectedRoute>} />
          <Route path="/admin/nds" element={<ProtectedRoute><NdsSchemaEditorPage /></ProtectedRoute>} />
          <Route path="/admin/roi" element={<ProtectedRoute><RoiDashboardPage /></ProtectedRoute>} />
          <Route path="/admin/pillar-health" element={<ProtectedRoute><PillarHealthPage /></ProtectedRoute>} />
          <Route path="/admin/archived-funds" element={<ProtectedRoute><ArchivedFundsPage /></ProtectedRoute>} />
          <Route path="/admin/archive-center" element={<ProtectedRoute><ArchiveCenterPage /></ProtectedRoute>} />
          <Route path="/admin/lesson-voice" element={<ProtectedRoute><LessonVoicePage /></ProtectedRoute>} />
          <Route path="/admin/db" element={<ProtectedRoute><DatabaseManagerPage /></ProtectedRoute>} />
          <Route path="/admin/demos" element={<ProtectedRoute><DemoDirectoryManagerPage /></ProtectedRoute>} />
          <Route path="/admin/pilot-requests" element={<ProtectedRoute><PilotRequestsPage /></ProtectedRoute>} />
          <Route path="/admin/forms" element={<ProtectedRoute><FormsPage /></ProtectedRoute>} />
          <Route path="/admin/records/:formId" element={<ProtectedRoute><RecordsPage /></ProtectedRoute>} />
          {/* The Exchange Alliance OS — universal core layout */}
          <Route path="/os" element={<ProtectedRoute><OsPage /></ProtectedRoute>} />
          {/* Public NDS-1 prospect demo — same layout as /os, sandboxed writes */}
          <Route path="/demo" element={<Navigate to="/" replace />} />
          <Route path="/watch" element={<Navigate to="/watch/the-platform" replace />} />
          <Route path="/watch/:slug" element={<WatchSegmentPage />} />
          {/* Client Demo Directory — public list → unlock → client page */}
          <Route path="/demos" element={<Navigate to="/" replace />} />
          <Route path="/demos/:slug/unlock" element={<DemoUnlockPage />} />
          <Route path="/demos/:slug" element={<ClientDemoPage />} />
          <Route path="/d/:token" element={<ShareLinkPage />} />
          <Route path="/demo/:slug/preview" element={<DemoPreviewPage />} />
          {/* Client preview */}
          <Route path="/new-brief" element={<Navigate to="/" replace />} />
          <Route path="/preview/:slug" element={<PreviewPublic />} />
          <Route path="/login" element={<SignInPage />} />
          <Route path="/login/resident-help" element={<PortalLoginPage />} />
          <Route path="/portal/resident" element={<ResidentPortalPage />} />
          <Route path="/partner/login" element={<SignInPage />} />
          <Route path="/vendor/login" element={<VendorLoginPage />} />
          <Route path="/vendor" element={<VendorPortalPage />} />
          <Route path="/admin/contract-vendors" element={<ProtectedRoute><ContractVendorsPage /></ProtectedRoute>} />
          <Route path="/admin/live-users" element={<ProtectedRoute><LiveUsersPage /></ProtectedRoute>} />
          <Route path="/consent/:token" element={<ConsentPage />} />
          <Route path="/welcome/:token" element={<ProspectWelcomePage />} />
          <Route path="/partner/set-pin" element={<PartnerSetPinPage />} />
          <Route path="/partner/set-password" element={<PartnerSetPinPage />} />
          <Route path="/partner" element={<Navigate to="/admin" replace />} />
          <Route path="/doc/sign" element={<DocSignPage />} />
          <Route path="/schedule" element={<PublicSchedulePage />} />
          <Route path="/schedule/cancel" element={<PublicScheduleCancelPage />} />
          {/* Checkout */}
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/checkout/cancel" element={<CheckoutCancel />} />
          <Route path="/privacy" element={<PrivacyPage />} />
        </Routes>
      ) : (
        <PublicShell bare={pathname === "/"}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/engines" element={<Navigate to="/" replace />} />
            <Route path="/case-study" element={<CaseStudyPage />} />
            <Route path="/program" element={<Navigate to="/" replace />} />
            <Route path="/pricing" element={<Navigate to="/" replace />} />
            <Route path="/founder" element={<Navigate to="/" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PublicShell>
      )}
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ContentProvider>
          <DynamicEngineProvider>
          <ScrollToTop />
          <AllyContextProvider>
            <AppRoutes />
            <PresencePinger />
            <AllyOS />
          </AllyContextProvider>
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#0F172A",
                border: "1px solid #334155",
                color: "#E2E8F0",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "12px",
                borderRadius: "0",
              },
            }}
          />
          </DynamicEngineProvider>
        </ContentProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
